import { defineComponent as _defineComponent } from 'vue'
import { computed, nextTick, ref, useAsync, useRoute, useStore, watch } from "@nuxtjs/composition-api";
import { type CLtkHeader } from "@rscollabs/capsule-wardrobe-components/src/components/CLtkHeader";
import { type NavItem } from "@rscollabs/capsule-wardrobe-components/src/components/CLtkHeader/types";

import SearchInput from "@/components/SearchInput.vue";
import { useAmplitude, useAuth, useI18n, useTranslate, useVuetify } from "@/composables";
import { isAuth0UserToken } from "@/schemes/auth0";
import { getAmplitudeStore } from "@/store/amplitude";
import { useCartsStore } from "@/store/carts";
import { filterRemoveUndefined } from "@/utils/transform/array";


export default /*#__PURE__*/_defineComponent({
  __name: 'NavBar',
  emits: ["search-click", "cart-click"],
  setup(__props, { emit }) {

const MAX_CART_NUMBER = 9;
const APPLY_TO_BE_A_CREATOR = "Apply to be a Creator";



const header = ref<typeof CLtkHeader>();
const searchInput = ref<typeof SearchInput | null>(null);
const isSearchMenuOpened = ref(false);

const amplitude = useAmplitude();
const auth = useAuth();
const i18n = useI18n();
const route = useRoute();
const store = useStore();
const { t } = useTranslate();
const vuetify = useVuetify();

const amplitudeStore = getAmplitudeStore(store);
const cartsStore = useCartsStore();

const fetch = async () => {
  if (auth.loggedIn) {
    await cartsStore.fetchCarts({
      includes: ["catalog_retailer_products", "product_details", "retailer_product_variants", "retailers"],
    });
  } else {
    cartsStore.resetState();
  }
};

useAsync(() => fetch(), "NavBar Fetch");

watch(
  () => auth.loggedIn,
  async (loggedIn) => {
    if (loggedIn) {
      await cartsStore.fetchCarts({
        includes: ["catalog_retailer_products", "product_details", "retailer_product_variants", "retailers"],
      });
    } else {
      cartsStore.resetState();
    }
  },
  { immediate: false },
);

watch(
  () => route.value.path,
  () => {
    if (vuetify.breakpoint.xsOnly && header.value) {
      header.value.isSearchMenuOpened = !!(route.value.path === "/search");
    }
  },
  {
    immediate: true,
  },
);

const isCartVisible = computed(() => {
  return auth.loggedIn;
});

const isEmailVisible = computed(() => {
  const user = auth.user;
  if (user === null) {
    return false;
  }

  // Conditionally shows the email when the user is not a Social IDP authenticated user
  if (isAuth0UserToken(user) && user["http://shopltk.com/profile"].authnCanChangeEmail === false) {
    return false;
  }

  return true;
});

const jwt = computed(() => {
  return auth.user?.jwt as ConsumerJwt | null;
});

const isImpersonating = computed(() => {
  return Boolean(jwt.value?.impersonator_id);
});

const email = computed(() => {
  return isImpersonating.value && (jwt.value as InfluencerJwt | null)?.email !== undefined
    ? (jwt.value as InfluencerJwt | null)?.email
    : auth.user?.email;
});

const isMobile = computed(() => vuetify.breakpoint.xsOnly);

const isLoggedIn = computed(() => auth.loggedIn);

const cartItemsCount = computed(() => {
  const count = Object.values(cartsStore.cartItems)
    .filter(filterRemoveUndefined)
    .reduce((prev, curr) => prev + curr.quantity, 0);

  if (count > MAX_CART_NUMBER) {
    return `${MAX_CART_NUMBER}+`;
  }
  return count;
});

const currentLanguage = computed({
  get() {
    return i18n?.locale;
  },
  set(newValue) {
    i18n.setLocale(newValue);
  },
});

const navItems = computed((): NavItem[] => {
  return vuetify.breakpoint.lgAndUp
    ? [
        {
          name: "Discover",
          title: t("Discover").value,
          to: "/home",
        },
        {
          name: "Shop",
          title: t("Shop").value,
          to: "/categories",
        },
        {
          name: "Deals",
          title: t("Deals").value,
          color: "error-50--text",
          children: [
            {
              name: "Sale Alert",
              title: t("Sale Alert").value,
              to: "/categories/salealert",
            },
            {
              name: "Finds Under $100",
              title: t("Finds Under $100").value,
              to: "/categories/findsunder100",
            },
            {
              name: "Finds Under $50",
              title: t("Finds Under $50").value,
              to: "/categories/findsunder50",
            },
          ],
        },
      ]
    : [
        {
          name: "Discover",
          title: t("Discover").value,
          to: "/home",
        },
        {
          name: "Shop",
          title: t("Shop").value,
          to: "/categories",
        },
        {
          name: "Deals",
          title: t("Deals").value,
          color: "error-50--text",
          children: [
            {
              name: "Sale Alert",
              title: t("Sale Alert").value,
              to: "/categories/salealert",
            },
            {
              name: "Finds Under $100",
              title: t("Finds Under $100").value,
              to: "/categories/findsunder100",
            },
            {
              name: "Finds Under $50",
              title: t("Finds Under $50").value,
              to: "/categories/findsunder50",
            },
          ],
        },
      ];
});

const userMenu = computed((): NavItem[] => {
  const items: NavItem[] = [
    {
      name: "Favorites",
      title: t("Favorites").value,
      to: "/account/favorites/products",
    },
    {
      name: "Settings",
      title: t("Settings").value,
      to: "/account",
    },
    {
      name: "Help Center",
      title: t("Help Center").value,
      href: "https://help.liketoknow.it/",
    },
    {
      name: APPLY_TO_BE_A_CREATOR,
      title: t("Apply to be a Creator").value,
      href: "https://creator.shopltk.com/apply/creator/home?utm_source=website&utm_medium=navigationCTA&utm_term=Apply+to+be+a+Creator&utm_content=shopltk_navigation",
    },
    {
      name: "Sign out",
      title: t("Sign out").value,
      to: "/logout",
    },
  ];

  if (isEmailVisible.value === true) {
    items.unshift({
      name: "Email",
      title: email.value ?? "",
    });
  }

  return items;
});

const loginMenu = computed((): NavItem[] => {
  return [
    {
      name: "Log in",
      icon: "capsule-profile",
      title: t("Log in").value,
      to: "/login",
    },
    {
      name: "Sign up",
      icon: "capsule-add-circle-outline",
      title: t("Sign up").value,
      to: "/signup",
    },
    {
      name: APPLY_TO_BE_A_CREATOR,
      title: t("Apply to be a Creator").value,
      href: "https://creator.shopltk.com/apply/creator/home?utm_source=website&utm_medium=navigationCTA&utm_term=Apply+to+be+a+Creator&utm_content=shopltk_navigation",
    },
  ];
});

const trackMenuClick = () => {
  amplitude?.sendEvent("/open-mobile-web-menu");
};

const onSearchBtnClick = (newIsSearchMenuOpened: boolean) => {
  isSearchMenuOpened.value = newIsSearchMenuOpened;
  if (isSearchMenuOpened.value) {
    nextTick(() => {
      searchInput.value?.input?.focus();
      emit("search-click");
    });
  }
};

const onSearchClose = () => {
  isSearchMenuOpened.value = false;
};

const onMenuItemClick = (item: NavItem) => {
  amplitudeStore.setRootReferrer(item?.name);
  amplitudeStore.setReferringComponent(vuetify.breakpoint.mdAndUp ? "desktop menu item" : "mobile web menu item");
  if (item.name === APPLY_TO_BE_A_CREATOR) {
    amplitude?.sendEvent("/creator-application-cta-clicked", {
      "referring-component": "mobile web menu item",
    });
  }
};

const onNavClick = (item: NavItem) => {
  amplitudeStore.setRootReferrer(item?.name);
  amplitudeStore.setReferringComponent(vuetify.breakpoint.mdAndUp ? "desktop nav item" : "mobile web nav item");
};

const onCartClick = () => {
  emit("cart-click");
};

return { __sfc: true,MAX_CART_NUMBER, APPLY_TO_BE_A_CREATOR, emit, header, searchInput, isSearchMenuOpened, amplitude, auth, i18n, route, store, t, vuetify, amplitudeStore, cartsStore, fetch, isCartVisible, isEmailVisible, jwt, isImpersonating, email, isMobile, isLoggedIn, cartItemsCount, currentLanguage, navItems, userMenu, loginMenu, trackMenuClick, onSearchBtnClick, onSearchClose, onMenuItemClick, onNavClick, onCartClick, SearchInput }
}

})